import { useThree } from '@react-three/fiber';
import { useEffect } from 'react';
import { Euler, Object3D, Vector3 } from 'three';

import type { EnhancedGLTF, LoadedCharacterMesh } from '@cartwhl/3d/gltf/types';
import {
  applyFaceExpression,
  applyShowSkeleton,
  applySkinHex,
  disableFrustumCulled,
  hideMeshMaterials,
  isMhrScene,
  sceneDeclaresYUp,
  sceneHasForgeAxisCorrection,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';

import useCharacterMeshWithCredentials, {
  type CharacterMeshFormat,
  type LoadFBXMesh,
  type TransformLoadedMesh,
} from '../hooks/useCharacterMeshWithCredentials';

export type CharacterRef = EnhancedGLTF | LoadedCharacterMesh;

/**
 * Character component that renders a character model (GLB/GLTF or FBX).
 * This is a pure component that shouldn't access stores.
 * Please refer to <ActiveCharacter> component if you want to display the current project's character.
 * When meshPath is a blob URL, pass meshFormat so the correct loader is used.
 */
export default function Character({
  characterID,
  meshPath,
  meshFormat,
  rotation,
  position,
  skinHex,
  faceExpression,
  showSkeleton,
  onSceneReady,
  onCharacterLoading,
  transformLoadedMesh,
  loadFBX,
  identityPath,
  actorColorKey,
}: {
  characterID: string;
  meshPath: string;
  /** Required when meshPath is a blob URL (no file extension). */
  meshFormat?: CharacterMeshFormat;
  rotation?: Euler;
  position?: Vector3;
  skinHex?: string;
  faceExpression?: string;
  showSkeleton?: boolean;
  onSceneReady?: (scene: Object3D) => void;
  onCharacterLoading?: (loading: boolean) => void;
  transformLoadedMesh?: TransformLoadedMesh;
  loadFBX?: LoadFBXMesh;
  /** Optional URL to a static morph identity JSON; applied once before render (MHR rigs only). */
  identityPath?: string;
  /** Stable actor key used for deterministic per-actor MHR coloring. */
  actorColorKey?: string;
}) {
  const invalidate = useThree((state) => state.invalidate);
  const meshData = useCharacterMeshWithCredentials(
    meshPath,
    onCharacterLoading,
    {
      format: meshFormat,
      modifiers: [hideMeshMaterials, turnOnShadows, disableFrustumCulled],
      transformLoadedMesh,
      loadFBX,
      identityPath,
    },
  );

  // Notify parent when scene is ready
  useEffect(() => {
    if (meshData?.scene && onSceneReady) {
      onSceneReady(meshData.scene as Object3D);
    }
  }, [meshData, onSceneReady]);

  useEffect(() => {
    if (meshData) {
      applySkinHex(skinHex, characterID, actorColorKey)(meshData);
      invalidate();
    }
  }, [actorColorKey, characterID, invalidate, meshData, skinHex]);

  useEffect(() => {
    if (meshData && faceExpression) {
      applyFaceExpression(faceExpression)(meshData);
    }
  }, [faceExpression, meshData]);

  useEffect(() => {
    if (meshData) {
      applyShowSkeleton(meshData, showSkeleton ?? false);
      invalidate();
    }
  }, [invalidate, showSkeleton, meshData]);

  if (!meshData) return null;

  // MHR, `up: 'Y'` GLBs, and forge robot GLBs (already axis-corrected) are Y-up;
  // skip the legacy -90° X correction so we don't double-rotate them onto their side.
  const resolvedRotation =
    isMhrScene(meshData.scene) ||
    sceneDeclaresYUp(meshData.scene) ||
    sceneHasForgeAxisCorrection(meshData.scene)
      ? undefined
      : rotation;

  return (
    <primitive
      object={meshData.scene}
      rotation={resolvedRotation}
      position={position}
    />
  );
}
